import type { ReactNode } from "react";

import { cn, hasRenderableNode } from "../base/utils";
import { useMonkeysLocale } from '../../provider/context';
import { renderDateTimeValue } from '../capabilities/dateTimeValue';

export interface WorkbenchEvidenceItem {
  id: string;
  title?: ReactNode;
  titleKey?: string;
  description?: ReactNode;
  descriptionKey?: string;
  source?: ReactNode;
  sourceKey?: string;
  timestamp?: ReactNode;
  timestampValue?: string | number;
  marker?: ReactNode;
  action?: ReactNode;
}

export interface WorkbenchEvidenceListProps {
  items: WorkbenchEvidenceItem[];
  emptyState?: ReactNode;
  ariaLabel?: string;
  className?: string;
  translate?: (key: string) => string;
}

export function WorkbenchEvidenceList({
  items,
  emptyState = null,
  ariaLabel = "Evidence",
  className,
  translate = key => key,
}: WorkbenchEvidenceListProps) {
  const { locale } = useMonkeysLocale();
  if (!items.length) {
    return hasRenderableNode(emptyState) ? <>{emptyState}</> : null;
  }

  return (
    <ol
      aria-label={ariaLabel}
      data-monkeys-component="workbench-evidence-list"
      className={cn("grid min-w-0 gap-0", className)}
    >
      {items.map((item, index) => (
        <li
          key={item.id}
          className="grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)_auto] gap-3"
        >
          <span className="flex flex-col items-center" aria-hidden="true">
            <span className="mt-1 flex size-5 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-[0.625rem] font-semibold text-primary">
              {item.marker || index + 1}
            </span>
            {index < items.length - 1 ? (
              <span className="min-h-8 w-px flex-1 bg-border" />
            ) : null}
          </span>
          <span className="min-w-0 pb-5">
            <span className="block text-sm font-semibold text-foreground">
              {item.titleKey ? translate(item.titleKey) : item.title}
            </span>
            {hasRenderableNode(item.description) || item.descriptionKey ? (
              <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                {item.descriptionKey ? translate(item.descriptionKey) : item.description}
              </span>
            ) : null}
            {hasRenderableNode(item.source) || item.sourceKey ||
            hasRenderableNode(item.timestamp) || item.timestampValue !== undefined ? (
              <span className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[0.6875rem] text-muted-foreground">
                {hasRenderableNode(item.source) || item.sourceKey ? (
                  <span>{item.sourceKey ? translate(item.sourceKey) : item.source}</span>
                ) : null}
                {hasRenderableNode(item.timestamp) || item.timestampValue !== undefined ? (
                  <time>{item.timestampValue !== undefined ? renderDateTimeValue(item.timestampValue, 'milliseconds', locale) : item.timestamp}</time>
                ) : null}
              </span>
            ) : null}
          </span>
          {hasRenderableNode(item.action) ? (
            <span className="pb-5">{item.action}</span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
